import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import type { IconName } from '@/components/Icon/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { IconButtonIconSize, IconButtonSize, IconButtonType, LinkTarget } from './types'

@safeCustomElement('mdc-icon-button')
export class MdcIconButton extends LitElement {
  @property({ type: String }) type: IconButtonType = 'floating'
  @property({ type: String }) size: IconButtonSize = 'md'
  @property({ type: String, attribute: 'icon-size' }) iconSize: IconButtonIconSize = 'default'
  @property({ type: String }) icon: IconName = 'checkmark'
  @property({ type: String }) href = ''
  @property({ type: String }) rel = ''
  @property({ type: String }) target: LinkTarget = '_self'
  @property({ type: String }) download: string | boolean = false
  @property({ type: String }) label = ''
  @property({ type: Boolean }) disabled = false

  static styles = css`
		:host {
			display: inline-flex;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-icon-button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: inherit;
			line-height: 0;
			text-align: center;
			margin: 0;
			border: none;
			border-radius: 50%;
			padding: 0;
			background-color: var(--color-background-Interactive-primary-default, var(--color-functional-gray-white));
			outline: none;
			transition: background-color 0.3s, border-color 0.3s, color 0.3s;
			font: inherit;
		}

		.mdc-icon-button > * {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: 0;
			vertical-align: middle;
		}

		.mdc-icon-button svg {
			display: block;
		}

		.mdc-icon-button:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-icon-button:disabled,
		.mdc-icon-button[aria-disabled='true'] {
			cursor: not-allowed;
			color: var(--color-functional-gray-300);
			pointer-events: none;
		}

		.mdc-icon-button--size-md {
			width: var(--spacing-200, 2rem);
			height: var(--spacing-200, 2rem);
		}

		.mdc-icon-button--size-sm {
			width: var(--spacing-150, 1.5rem);
			height: var(--spacing-150, 1.5rem);
			padding: 0.25rem;
		}

		.mdc-icon-button--size-lg {
			width: var(--spacing-250, 2.5rem);
			height: var(--spacing-250, 2.5rem);
			padding: 0.625rem;
		}

		.mdc-icon-button--icon-size-default svg {
			width: var(--spacing-100, 1rem);
			height: var(--spacing-100, 1rem);
		}

		.mdc-icon-button--icon-size-smaller svg {
			width: var(--spacing-087-and-half, 0.875rem);
			height: var(--spacing-087-and-half, 0.875rem);
		}

		.mdc-icon-button--floating {
			color: var(--color-functional-gray-black);
			background-color: var(--color-functional-gray-white);
			box-shadow: var(--shadow-floating-sm, 0px 0px 8px 0px rgba(0, 0, 0, 0.16));
		}

		.mdc-icon-button--floating:hover {
			color: var(--color-functional-gray-400);
			box-shadow: var(--shadow-floating-md, 0px 0px 16px 0px rgba(0, 0, 0, 0.08));
		}

		.mdc-icon-button--floating:focus-visible {
			color: var(--color-functional-gray-400);
		}

		.mdc-icon-button--secondary {
			border: 1px solid var(--color-functional-gray-75);
		}

		.mdc-icon-button--secondary:hover {
			border-color: var(--color-functional-gray-200);
		}

		.mdc-icon-button--secondary:focus-visible {
			border-color: var(--color-functional-gray-200);
		}

		.mdc-icon-button--tertiary {
			color: var(--color-functional-gray-black);
		}

		.mdc-icon-button--tertiary:hover,
		.mdc-icon-button--tertiary:focus-visible {
			background-color: var(--color-functional-gray-25);
		}

		.mdc-icon-button--inverse-tertiary {
			color: var(--color-functional-gray-white);
		}

		.mdc-icon-button--inverse-tertiary:hover,
		.mdc-icon-button--inverse-tertiary:focus-visible {
			background-color: rgba(255, 255, 255, 0.1);
		}

		.mdc-icon-button--primary {
			color: var(--color-functional-gray-white);
			background-color: var(--color-functional-gray-black);
		}

		.mdc-icon-button--primary:hover,
		.mdc-icon-button--primary:focus-visible {
			background-color: var(--color-functional-gray-800);
		}

		.mdc-icon-button--primary:disabled,
		.mdc-icon-button--primary[aria-disabled='true'] {
			color: var(--color-functional-gray-300);
			background-color: var(--color-functional-gray-50);
		}
	`

  private handleClick(e: Event) {
    if (this.disabled) {
      e.preventDefault()
      return
    }
    this.dispatchEvent(
      new CustomEvent('click', {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const className = `mdc-icon-button mdc-icon-button--${this.type} mdc-icon-button--size-${this.size} mdc-icon-button--icon-size-${this.iconSize}`
    const iconElement = html`<mdc-icon name=${this.icon}></mdc-icon>`

    if (this.href) {
      return html`
				<a
					class=${className}
					href=${this.href}
					?disabled=${this.disabled}
					aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
					aria-label=${ifDefined(this.label || undefined)}
					title=${ifDefined(this.label || undefined)}
					rel=${ifDefined(this.rel || undefined)}
					target=${ifDefined(this.target === '_self' ? undefined : this.target)}
					download=${this.download !== false ? (this.download === true ? '' : this.download) : nothing}
					@click=${this.handleClick}
				>
					${iconElement}
				</a>
			`
    }

    return html`
			<button
				type="button"
				class=${className}
				?disabled=${this.disabled}
				aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
				aria-label=${ifDefined(this.label || undefined)}
				title=${ifDefined(this.label || undefined)}
				@click=${this.handleClick}
			>
				${iconElement}
			</button>
		`
  }
}
